'use client';

import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useJobs } from '@gate-js/react';

function getFieldLabels(value) {
	if (!value) return [];
	if (typeof value === 'string') return [value];
	if (Array.isArray(value)) return value.map((v) => v.label);
	return [value.label];
}

function JobFilterSidebarField({ field, color, value = [], onChange, jobs }) {
	const handleToggle = (option) => {
		const newValue = value.includes(option)
			? value.filter((v) => v !== option)
			: [...value, option];
		onChange(newValue);
	};

	return (
		<div className="mb-6">
			<div className="text-sm font-semibold mb-3" style={{ color }}>
				{field.label}
			</div>
			<ul className="space-y-2">
				{field.options.map((option) => {
					const count = jobs
						? jobs.filter((job) => getFieldLabels(job.fields[field.name]).includes(option)).length
						: null;
					return (
						<li key={option} className="flex items-center gap-2">
							<input
								type="checkbox"
								id={`filter-${field.name}-${option}`}
								checked={value.includes(option)}
								onChange={() => handleToggle(option)}
								className="h-4 w-4 rounded border-gray-300 cursor-pointer"
							/>
							<label
								htmlFor={`filter-${field.name}-${option}`}
								className="text-left text-sm text-gray-700 cursor-pointer select-none flex items-center gap-1"
							>
								{option}
								{count !== null && (
									<span className="text-xs text-gray-400">({count})</span>
								)}
							</label>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export function JobFilterSidebar({ filterBy, fields, color, className, style }) {
	const { setFilter, filter = {}, jobs } = useJobs();
	const allJobsRef = useRef(null);
	if (jobs && !allJobsRef.current) allJobsRef.current = jobs;
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	if (!fields || fields.length === 0) return null;

	const filteredFields = fields.filter((f) => filterBy.includes(f.name));

	if (!mounted) {
		return (
			<div className={classNames('w-52 shrink-0 animate-pulse px-2 py-3', className)} style={style}>
				{filteredFields.map((field) => (
					<div key={field.name} className="mb-6">
						<div className="h-3 w-24 bg-gray-200 rounded mb-3" />
						{field.options.slice(0, 5).map((opt) => (
							<div key={opt} className="flex items-center gap-2 mb-2">
								<div className="h-4 w-4 bg-gray-200 rounded" />
								<div className="h-3 w-28 bg-gray-100 rounded" />
							</div>
						))}
					</div>
				))}
			</div>
		);
	}

	const handleFilterChange = (fieldName, values) => {
		const newCustom = { ...filter.custom };
		if (!values || values.length === 0) {
			delete newCustom[fieldName];
		} else {
			newCustom[fieldName] = values;
		}
		setFilter({ ...filter, custom: newCustom });
	};

	return (
		<div className={classNames('w-52 shrink-0 px-2 py-3', className)} style={style}>
			{filteredFields.map((field) => (
				<JobFilterSidebarField
					key={field.name}
					field={field}
					color={color}
					value={filter.custom?.[field.name] || []}
					onChange={(values) => handleFilterChange(field.name, values)}
					jobs={allJobsRef.current}
				/>
			))}
		</div>
	);
}
